/**
 * Trending on TikTok — the top commercial sounds per region (GET /trends/audio, TikTok Commercial Music Library) as a
 * grid of cards (TrendCard: the song's top TikTok video in a 9:16 media area — its cover when there is none — rank,
 * status, curve, "Play song", "Remix"). From 1024px the section is a subgrid of three rows shared with
 * "Recent creations" so both columns line up: A. title + rights note (+ the market insight as plain text); B. the toolbar —
 * region, period, "Updated …"; C. the cards.
 * "Remix" calls `onRemix` (the page opens the Remix dialog); opening a card (body or ›) calls `onOpen`, which the page
 * shows on the right-hand stage (desktop) or in TrendSongDrawer (smaller screens). Covers that are missing or shared by
 * ≥ 2 songs (TikTok's default artwork) are replaced by a generated genre tile.
 */
import { useMemo, useState } from 'react';
import { cn } from '@/components/ui/utils';
import type { AudioTrend } from '@/api/audioStudioApi';
import { formatDate } from './format';
import { useAudioTrends } from './queries';
import { trendsRightsNote } from './rights';
import { TrendCard } from './TrendCard';
import { DEFAULT_TREND_REGION } from '@/api/audioStudioApi';
import { TREND_PERIODS, marketInsight, needsPlaceholderCover, periodPhrase, sharedCoverUrls, type TrendFocus, type TrendPeriod } from './trendInsights';

// Every market TikTok's Commercial Music Library ranks separately (checked against the live API, Oct 2026; China, Russia
// and India answer too but TikTok isn't available there). Trends are fetched per market only when someone opens it.
const TREND_REGIONS = [
  { id: 'GB', label: 'United Kingdom', country: 'the UK', group: 'Europe' },
  { id: 'AT', label: 'Austria', country: 'Austria', group: 'Europe' },
  { id: 'BE', label: 'Belgium', country: 'Belgium', group: 'Europe' },
  { id: 'CZ', label: 'Czechia', country: 'Czechia', group: 'Europe' },
  { id: 'DK', label: 'Denmark', country: 'Denmark', group: 'Europe' },
  { id: 'FI', label: 'Finland', country: 'Finland', group: 'Europe' },
  { id: 'FR', label: 'France', country: 'France', group: 'Europe' },
  { id: 'DE', label: 'Germany', country: 'Germany', group: 'Europe' },
  { id: 'GR', label: 'Greece', country: 'Greece', group: 'Europe' },
  { id: 'HU', label: 'Hungary', country: 'Hungary', group: 'Europe' },
  { id: 'IS', label: 'Iceland', country: 'Iceland', group: 'Europe' },
  { id: 'IE', label: 'Ireland', country: 'Ireland', group: 'Europe' },
  { id: 'IT', label: 'Italy', country: 'Italy', group: 'Europe' },
  { id: 'NL', label: 'Netherlands', country: 'the Netherlands', group: 'Europe' },
  { id: 'NO', label: 'Norway', country: 'Norway', group: 'Europe' },
  { id: 'PL', label: 'Poland', country: 'Poland', group: 'Europe' },
  { id: 'PT', label: 'Portugal', country: 'Portugal', group: 'Europe' },
  { id: 'RO', label: 'Romania', country: 'Romania', group: 'Europe' },
  { id: 'ES', label: 'Spain', country: 'Spain', group: 'Europe' },
  { id: 'SE', label: 'Sweden', country: 'Sweden', group: 'Europe' },
  { id: 'CH', label: 'Switzerland', country: 'Switzerland', group: 'Europe' },
  { id: 'UA', label: 'Ukraine', country: 'Ukraine', group: 'Europe' },
  { id: 'US', label: 'United States', country: 'the US', group: 'Americas' },
  { id: 'AR', label: 'Argentina', country: 'Argentina', group: 'Americas' },
  { id: 'BR', label: 'Brazil', country: 'Brazil', group: 'Americas' },
  { id: 'CA', label: 'Canada', country: 'Canada', group: 'Americas' },
  { id: 'CL', label: 'Chile', country: 'Chile', group: 'Americas' },
  { id: 'CO', label: 'Colombia', country: 'Colombia', group: 'Americas' },
  { id: 'MX', label: 'Mexico', country: 'Mexico', group: 'Americas' },
  { id: 'PE', label: 'Peru', country: 'Peru', group: 'Americas' },
  { id: 'AU', label: 'Australia', country: 'Australia', group: 'Asia-Pacific' },
  { id: 'ID', label: 'Indonesia', country: 'Indonesia', group: 'Asia-Pacific' },
  { id: 'JP', label: 'Japan', country: 'Japan', group: 'Asia-Pacific' },
  { id: 'MY', label: 'Malaysia', country: 'Malaysia', group: 'Asia-Pacific' },
  { id: 'NZ', label: 'New Zealand', country: 'New Zealand', group: 'Asia-Pacific' },
  { id: 'PH', label: 'Philippines', country: 'the Philippines', group: 'Asia-Pacific' },
  { id: 'SG', label: 'Singapore', country: 'Singapore', group: 'Asia-Pacific' },
  { id: 'KR', label: 'South Korea', country: 'South Korea', group: 'Asia-Pacific' },
  { id: 'TH', label: 'Thailand', country: 'Thailand', group: 'Asia-Pacific' },
  { id: 'VN', label: 'Vietnam', country: 'Vietnam', group: 'Asia-Pacific' },
  { id: 'EG', label: 'Egypt', country: 'Egypt', group: 'Middle East & Africa' },
  { id: 'IL', label: 'Israel', country: 'Israel', group: 'Middle East & Africa' },
  { id: 'KE', label: 'Kenya', country: 'Kenya', group: 'Middle East & Africa' },
  { id: 'NG', label: 'Nigeria', country: 'Nigeria', group: 'Middle East & Africa' },
  { id: 'SA', label: 'Saudi Arabia', country: 'Saudi Arabia', group: 'Middle East & Africa' },
  { id: 'ZA', label: 'South Africa', country: 'South Africa', group: 'Middle East & Africa' },
  { id: 'TR', label: 'Turkey', country: 'Turkey', group: 'Middle East & Africa' },
  { id: 'AE', label: 'United Arab Emirates', country: 'the UAE', group: 'Middle East & Africa' },
] as const;

const TREND_REGION_GROUPS = ['Europe', 'Americas', 'Asia-Pacific', 'Middle East & Africa'] as const;

const GRID_CLS = 'grid grid-cols-[repeat(auto-fill,minmax(176px,1fr))] gap-4';

/** Light grey placeholders shaped like the cards (9:16 media + three lines + Play song + Remix). */
function TrendsSkeleton() {
  return (
    <div role="status" aria-label="Loading TikTok trends">
      <span className="sr-only">Loading TikTok trends…</span>
      <ul className={GRID_CLS} aria-hidden>
        {Array.from({ length: 8 }, (_, i) => (
          <li key={i} className="w-full overflow-hidden rounded-[12px] border border-[#f2f4f7] bg-white">
            <div className="aspect-[9/16] w-full animate-pulse bg-[#f2f4f7]" />
            <div className="flex flex-col gap-1 p-3">
              <div className="h-5 w-3/4 animate-pulse rounded bg-[#f2f4f7]" />
              <div className="h-[18px] w-1/2 animate-pulse rounded bg-[#f2f4f7]" />
              <div className="mt-2 h-8 w-full animate-pulse rounded-[8px] bg-[#f9fafb]" />
              <div className="h-8 w-full animate-pulse rounded-[8px] bg-[#f9fafb]" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TrendsSection({ selectedId = null, onRemix, onOpen }: {
  /** trend_id shown on the stage (ringed card). */
  selectedId?: string | null;
  onRemix: (focus: TrendFocus) => void;
  onOpen: (focus: TrendFocus) => void;
}) {
  const [region, setRegion] = useState<string>(DEFAULT_TREND_REGION);
  const [period, setPeriod] = useState<TrendPeriod>(7);
  const trends = useAudioTrends(region, period);
  const items = useMemo(() => trends.data?.items ?? [], [trends.data]);
  const shared = useMemo(() => sharedCoverUrls(items), [items]);
  const insight = useMemo(() => marketInsight(items), [items]);
  const country = TREND_REGIONS.find((r) => r.id === region)?.country ?? region;
  const focusOf = (t: AudioTrend): TrendFocus => ({ trend: t, region, period, placeholderCover: needsPlaceholderCover(t, shared) });

  return (
    <section aria-labelledby="audio-trends-title" className="min-w-0 lg:col-start-1 lg:row-span-3 lg:row-start-1 lg:grid lg:grid-rows-subgrid">
      {/* Row A: title + notes (shared with "Recent creations"). */}
      <div id="audio-trends" className="min-w-0 scroll-mt-6">
        <h2 id="audio-trends-title" className="h-7 truncate text-[17px] font-semibold leading-7 text-[#101828]">
          Trending on TikTok
        </h2>
        <p className="mt-1 text-[12.5px] leading-[18px] text-[#667085]">{trendsRightsNote(trends.data?.source)}</p>
        {insight && !trends.isLoading && !trends.isError && (
          <p className="text-[12.5px] leading-[18px] text-[#667085]" data-testid="market-insight">
            {insight.kind === 'rising' ? 'Rising' : 'Trending'} in {country} {periodPhrase(period)}:{' '}
            <span className="text-[#344054]" style={{ fontWeight: 500 }}>{insight.genres.join(' · ')}</span>
          </p>
        )}
      </div>

      {/* Row B: toolbar. */}
      <div data-toolbar="trends" className="mt-3 flex min-h-8 flex-wrap items-center gap-2">
        <select value={region} onChange={(e) => setRegion(e.target.value)} aria-label="Region" className="h-8 rounded-[8px] border border-[#eaecf0] bg-white px-2.5 text-[12.5px] text-[#344054]">
          {TREND_REGION_GROUPS.map((g) => (
            <optgroup key={g} label={g}>
              {TREND_REGIONS.filter((r) => r.group === g).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </optgroup>
          ))}
        </select>
        <div role="radiogroup" aria-label="Period" className="inline-flex h-8 w-fit items-center rounded-[8px] bg-[#f2f4f7] p-0.5">
          {TREND_PERIODS.map((p) => (
            <button key={p.days} type="button" role="radio" aria-checked={period === p.days} onClick={() => setPeriod(p.days)} className={cn('h-7 rounded-[6px] px-3 text-[12.5px] leading-[18px] transition-colors', period === p.days ? 'bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.06)]' : 'text-[#667085] hover:text-[#344054]')} style={{ fontWeight: 500 }}>
              {p.label}
            </button>
          ))}
        </div>
        {trends.data?.fetched_at && <span className="ml-auto shrink-0 text-[11.5px] leading-4 text-[#98a2b3]">Updated {formatDate(trends.data.fetched_at)}</span>}
      </div>

      {/* Row C: the cards — they start at the same y as the Recent creations panel. */}
      <div className="mt-4 min-w-0">
        {trends.isLoading ? (
          <TrendsSkeleton />
        ) : trends.isError ? (
          <div className="rounded-[12px] border border-[#eaecf0] bg-white py-16 text-center text-[12.5px] text-[#b42318]" role="alert">Could not load trends: {(trends.error as Error).message}</div>
        ) : !items.length ? (
          <div className="rounded-[12px] border border-[#eaecf0] bg-white py-16 text-center text-[12.5px] text-[#98a2b3]">No trending sounds for this region yet.</div>
        ) : (
          <ul className={GRID_CLS} aria-label="Trending songs">
            {items.map((t) => (
              <li key={t.trend_id} className="min-w-0">
                <TrendCard
                  t={t}
                  region={region}
                  selected={selectedId === t.trend_id}
                  placeholderCover={needsPlaceholderCover(t, shared)}
                  onOpen={() => onOpen(focusOf(t))}
                  onRemix={() => onRemix(focusOf(t))}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
